import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Plus, Play, PlugZap, Ban, Send, Megaphone, CheckCircle2, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { EmptyState } from '@/components/shared/feedback'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { apiError } from '@/lib/api-client'
import { useMyPermissions } from '@/hooks/use-settings'
import { ProgressBar } from './progress-bar'
import { CreateCampaignDialog } from './create-campaign-dialog'
import { ZnsTemplatesTab } from './zns-templates-tab'
import {
  useZnsCampaigns,
  useOaAccounts,
  useStartZnsCampaign,
  useCancelZnsCampaign,
  campaignStatusLabel,
  campaignStatusVariant,
  CAMPAIGN_STATUS_LABELS,
  type ZnsCampaign,
} from '@/hooks/use-zns'

const ALL = '__all__'

export function ZnsCampaignsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')
  const [createOpen, setCreateOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>(ALL)

  const params = useMemo(
    () => (statusFilter === ALL ? { limit: 100 } : { status: statusFilter, limit: 100 }),
    [statusFilter],
  )
  const { data: campaigns, isLoading, isError } = useZnsCampaigns(params)

  const accountsQuery = useOaAccounts()
  // Chỉ coi là "chưa kết nối" khi đã tải xong và danh sách rỗng (tránh nháy khi đang tải).
  const noOa = accountsQuery.isSuccess && (accountsQuery.data?.length ?? 0) === 0

  const startMutation = useStartZnsCampaign()
  const cancelMutation = useCancelZnsCampaign()
  // Tạo / Bắt đầu / Hủy chiến dịch → BE đòi zns.send (Nhân viên mặc định chỉ có zns.view) → ẩn hẳn (BRD §5.3.1).
  // Kết nối OA → integrations.create.
  const { data: perms } = useMyPermissions()
  const canSend = !!perms?.has('zns.send')
  const canConnectOa = !!perms?.has('integrations.create')

  const stats = useMemo(() => {
    const list = campaigns ?? []
    return {
      total: list.length,
      running: list.filter((c) => c.status === 'running' || c.status === 'queued').length,
      completed: list.filter((c) => c.status === 'completed').length,
      sent: list.reduce((sum, c) => sum + (c.sentCount ?? 0), 0),
    }
  }, [campaigns])

  async function handleStart(c: ZnsCampaign) {
    try {
      const res = await startMutation.mutateAsync(c.id)
      toast.success(t('znsCampaigns.startedToast', { enqueued: res.enqueued, skipped: res.skipped }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleCancel(c: ZnsCampaign) {
    try {
      await cancelMutation.mutateAsync(c.id)
      toast.success(t('znsCampaigns.cancelledToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const columns: Column<ZnsCampaign>[] = [
    {
      key: 'name',
      header: t('znsCampaigns.columns.name'),
      cell: (c) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{c.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {c.channelAccount?.displayName || t('znsCampaigns.oaFallback')} · {c.createdBy?.fullName || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'template',
      header: t('znsCampaigns.columns.template'),
      cell: (c) => <span className="tabular-nums text-muted-foreground">{c.templateId}</span>,
    },
    {
      key: 'status',
      header: t('znsCampaigns.columns.status'),
      cell: (c) => <Badge variant={campaignStatusVariant(c.status)}>{campaignStatusLabel(c.status)}</Badge>,
    },
    {
      key: 'progress',
      header: t('znsCampaigns.columns.progress'),
      className: 'w-[200px]',
      cell: (c) => {
        const done = c.sentCount + c.failedCount + c.skippedCount
        const tone = c.failedCount > 0 ? 'warning' : c.status === 'completed' ? 'success' : 'primary'
        return (
          <div className="space-y-1">
            <ProgressBar value={done} total={c.totalCount} tone={tone} />
            <p className="text-xs text-muted-foreground tabular-nums">
              {t('znsCampaigns.sentCount', { sent: c.sentCount, total: c.totalCount })}
              {c.failedCount > 0 && ` · ${t('znsCampaigns.failedCount', { count: c.failedCount })}`}
            </p>
          </div>
        )
      },
    },
    {
      key: 'scheduledAt',
      header: t('znsCampaigns.columns.scheduledAt'),
      cell: (c) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {c.scheduledAt ? dayjs(c.scheduledAt).format('DD/MM/YYYY HH:mm') : '—'}
        </span>
      ),
    },
    ...(canSend ? [{
      key: 'actions',
      header: '',
      align: 'right' as const,
      cell: (c: ZnsCampaign) => {
        const canStart = c.status === 'draft' || c.status === 'queued'
        const canCancel = c.status === 'running' || c.status === 'queued'
        return (
          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            {canStart && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStart(c)}
                disabled={startMutation.isPending}
              >
                <Play className="h-4 w-4" /> {t('znsCampaigns.start')}
              </Button>
            )}
            {canCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCancel(c)}
                disabled={cancelMutation.isPending}
              >
                <Ban className="h-4 w-4" /> {t('znsCampaigns.cancel')}
              </Button>
            )}
          </div>
        )
      },
    }] : []),
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('znsCampaigns.title')}
        description={t('znsCampaigns.description')}
        actions={
          canSend && (
            <Button onClick={() => setCreateOpen(true)} disabled={noOa}>
              <Plus className="h-4 w-4" /> {t('znsCampaigns.create')}
            </Button>
          )
        }
      />

      {noOa && (
        <EmptyState
          icon={PlugZap}
          title={t('znsCampaigns.noOa.title')}
          description={t('znsCampaigns.noOa.description')}
          action={
            canConnectOa && (
              <Button onClick={() => navigate('/integrations/zalo-oa')}>
                <PlugZap className="h-4 w-4" /> {t('znsCampaigns.noOa.connect')}
              </Button>
            )
          }
        />
      )}

      <Tabs defaultValue="campaigns" className={noOa ? 'hidden' : undefined}>
        <TabsList>
          <TabsTrigger value="campaigns">{t('znsCampaigns.tabs.campaigns')}</TabsTrigger>
          <TabsTrigger value="templates">{t('znsCampaigns.tabs.templates')}</TabsTrigger>
        </TabsList>

        {/* ── Tab: Chiến dịch ─────────────────────────────────────────── */}
        <TabsContent value="campaigns" className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label={t('znsCampaigns.stats.total')} value={stats.total} icon={Megaphone} />
            <StatCard label={t('znsCampaigns.stats.running')} value={stats.running} icon={Loader2} tone="warning" />
            <StatCard label={t('znsCampaigns.stats.completed')} value={stats.completed} icon={CheckCircle2} tone="success" />
            <StatCard label={t('znsCampaigns.stats.sent')} value={stats.sent} icon={Send} tone="primary" />
          </div>

          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('znsCampaigns.columns.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('znsCampaigns.allStatuses')}</SelectItem>
                {Object.entries(CAMPAIGN_STATUS_LABELS).map(([v, label]) => (
                  <SelectItem key={v} value={v}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DataTable
            columns={columns}
            rows={campaigns ?? []}
            loading={isLoading}
            rowKey={(c) => c.id}
            onRowClick={(c) => navigate(`/zns-campaigns/${c.id}`)}
            emptyTitle={isError ? t('znsCampaigns.loadError') : t('znsCampaigns.empty')}
          />
        </TabsContent>

        {/* ── Tab: Mẫu tin ────────────────────────────────────────────── */}
        <TabsContent value="templates">
          <ZnsTemplatesTab />
        </TabsContent>
      </Tabs>

      {canSend && <CreateCampaignDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  )
}
